import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Download, FileSpreadsheet, Plus, Upload } from "lucide-react";
import { api, downloadFile, errText, fmtUSD, fmtUSD2 } from "@/lib/apiClient";
import { useDicts } from "@/hooks/useDicts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import OperationDialog from "@/components/OperationDialog";

export default function PnL() {
  const [range, setRange] = useState({ ...presetRange("month"), preset: "month" });
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const { dicts, reload } = useDicts();
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/pnl", { params: { date_from: range.from, date_to: range.to } });
    setRows(data);
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/operations/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Импортировано операций: ${data.created}`);
      if (data.errors?.length) toast.error(`Ошибки: ${data.errors.slice(0, 3).join("; ")}`);
      reload();
      load();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      e.target.value = "";
    }
  };

  const total = rows.reduce(
    (a, r) => ({
      balance: a.balance + r.balance,
      income: a.income + r.income,
      expense: a.expense + r.expense,
      income_planned: a.income_planned + r.income_planned,
      expense_planned: a.expense_planned + r.expense_planned,
    }),
    { balance: 0, income: 0, expense: 0, income_planned: 0, expense_planned: 0 }
  );

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">P&amp;L по компаниям</h1>
          <p className="mt-1 text-sm text-muted-foreground">Факт на сегодня и ожидаемые суммы за выбранный период</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" data-testid="pnl-template"
            onClick={() => downloadFile("/operations/import-template", {}, "mayo_import_template.xlsx")}>
            <FileSpreadsheet className="h-4 w-4" /> Шаблон
          </Button>
          <Button variant="outline" className="gap-2" data-testid="pnl-import" onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4" /> Импорт Excel
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="pnl-import-input" onChange={importFile} />
          <Button variant="outline" className="gap-2" data-testid="pnl-export-excel"
            onClick={() => downloadFile("/operations/export", { format: "excel", date_from: range.from, date_to: range.to }, "mayo_pnl.xlsx")}>
            <Download className="h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" className="gap-2" data-testid="pnl-export-pdf"
            onClick={() => downloadFile("/operations/export", { format: "pdf", date_from: range.from, date_to: range.to }, "mayo_pnl.pdf")}>
            <Download className="h-4 w-4" /> PDF
          </Button>
          <Button className="gap-2" data-testid="pnl-new-operation" onClick={() => setDialog(true)}>
            <Plus className="h-4 w-4" /> Операция
          </Button>
        </div>
      </div>

      <PeriodPicker range={range} onChange={setRange} />

      <div className="grid gap-4 md:grid-cols-4">
        {[
          ["Суммарный остаток", fmtUSD(total.balance), "total-balance"],
          ["Заработано", fmtUSD(total.income), "total-income"],
          ["Потрачено", fmtUSD(total.expense), "total-expense"],
          ["Ожидается (дох./расх.)", `${fmtUSD(total.income_planned)} / ${fmtUSD(total.expense_planned)}`, "total-planned"],
        ].map(([label, value, tid]) => (
          <Card className="glass metric-card" key={tid} data-testid={`pnl-${tid}`}>
            <CardContent className="p-5">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
              <div className="mt-2 font-mono text-xl font-bold lg:text-2xl">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {rows.map((r) => (
          <Link key={r.id} to={`/pnl/${r.id}`} data-testid={`pnl-company-${r.id}`}>
            <Card className="glass metric-card h-full">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{r.name}</h3>
                    <div className="text-xs text-muted-foreground">Основная валюта: {r.currency || "USD"}</div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="mt-4 font-mono text-2xl font-bold">{fmtUSD2(r.balance)}</div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Заработано</div>
                    <div className="font-mono text-income">{fmtUSD(r.income)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Потрачено</div>
                    <div className="font-mono text-expense">{fmtUSD(r.expense)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">в т.ч. переводы</div>
                    <div className="font-mono text-transfer">
                      +{fmtUSD(r.transfer_in)} / −{fmtUSD(r.transfer_out)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Ожидается</div>
                    <div className="font-mono text-warning">
                      +{fmtUSD(r.income_planned)} / −{fmtUSD(r.expense_planned)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">{r.count} операций за период</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <OperationDialog open={dialog} onOpenChange={setDialog} dicts={dicts} reloadDicts={reload} onSaved={load} />
    </div>
  );
}
